import React, { useState } from 'react';
import { format } from 'date-fns';
import QRCode from 'qrcode';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { exportQrCodeAsPng } from 'src/utils/qr-code-generator';
import { generateQrCodePdf } from 'src/utils/pdf-generator';
import type { QrCodeMetadata } from 'src/types/qr-codes';

// ----------------------------------------------------------------------

interface QrCodeExportPanelProps {
  metadata: QrCodeMetadata;
  qrCodeDataUrl: string; // Base64 data URL from canvas
}

// ----------------------------------------------------------------------

/**
 * QR Code Export Panel Component
 * Displays QR code metadata and provides download buttons for PNG and PDF export
 */
export function QrCodeExportPanel({ metadata, qrCodeDataUrl }: QrCodeExportPanelProps) {
  const [pngLoading, setPngLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExportPng = async () => {
    try {
      setPngLoading(true);
      setError(null);
      await exportQrCodeAsPng(metadata.encryptedQrCode, metadata.branchName);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'PNG export failed';
      setError(errorMessage);
    } finally {
      setPngLoading(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      setError(null);

      // Generate a fresh QR code data URL using the qrcode library
      // This is more reliable than using the canvas ref
      const freshDataUrl = await QRCode.toDataURL(metadata.encryptedQrCode, {
        width: 512,
        margin: 2,
        errorCorrectionLevel: 'M',
        type: 'image/png',
      });

      await generateQrCodePdf(metadata, freshDataUrl);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'PDF export failed';
      setError(errorMessage);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        {/* Header */}
        <Typography variant="h6">QR Kod Bilgileri</Typography>

        <Divider />

        {/* Metadata Display */}
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Şube
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {metadata.branchName}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Adres
            </Typography>
            <Typography variant="body2">{metadata.branchAddress}</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Oluşturulma Tarihi
            </Typography>
            <Typography variant="body2">
              {format(new Date(metadata.generatedAt), 'dd/MM/yyyy HH:mm')}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Son Kullanma Tarihi
            </Typography>
            <Typography variant="body2" color={metadata.isExpired ? 'error.main' : 'inherit'}>
              {metadata.expiresAt
                ? format(new Date(metadata.expiresAt), 'dd/MM/yyyy HH:mm')
                : 'Süresiz'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Konum Aralığı
            </Typography>
            <Typography variant="body2">{metadata.locationRangeMeters} metre</Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Oluşturan
            </Typography>
            <Typography variant="body2">{metadata.generatedByName}</Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Error Display */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Export Buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={
              pngLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Iconify icon="eva:download-fill" />
              )
            }
            onClick={handleExportPng}
            disabled={pngLoading || pdfLoading}
            fullWidth
          >
            PNG İndir
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={
              pdfLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Iconify icon="eva:file-text-fill" />
              )
            }
            onClick={handleExportPdf}
            disabled={pngLoading || pdfLoading}
            fullWidth
          >
            PDF İndir
          </Button>
        </Stack>

        <Typography variant="caption" color="text.secondary" textAlign="center">
          QR kodu yazdırmak için PDF formatını, dijital kaydetmek için PNG formatını kullanın.
        </Typography>
      </Stack>
    </Paper>
  );
}
